/**
 * WHICH MAILBOX A LEG USES, and — when it names none — what that actually means.
 *
 * WHY THIS FILE EXISTS
 *
 * A job may now name a `mail`-kind provider account for its OUTBOUND leg as well as its
 * inbound one, because a provider's send confirmation does not always arrive in the same
 * mailbox as its received faxes: on SRFax they are different mailboxes owned by different
 * entities, while SFax and eFax deliver both into ONE box. Neither shape is the normal case
 * with the other as an exception — the operator's ruling is that *"notifications land in
 * either permutation of job defined and related account configs"* — so this module phrases
 * all four permutations rather than optimising for one:
 *
 *   1. same mailbox, only the inbound leg naming it (eFax / SFax today, NO outbound mail
 *      account configured — the backward-compatibility case);
 *   2. same mailbox, BOTH legs naming it (explicitly valid, NOT a warning state);
 *   3. different mailboxes (SRFax);
 *   4. an outbound mailbox with NO inbound mailbox at all — the permutation nothing visibly
 *      breaks on, because such a job saves, sends, and simply never collects its
 *      notification.
 *
 * THE RULE IT ENFORCES is `outboundHandle.ts`'s and `apiEndpoint.ts`'s: never present a value
 * as belonging to a leg that cannot be shown to have supplied it, and NAME THE GAP instead of
 * borrowing a neighbour's default. Two specific misreadings are what this prevents:
 *
 *   - an outbound leg naming no mailbox rendered BLANK beside an inbound row that names one,
 *     which reads as "the inbound mailbox is the outbound one too" — true for eFax, false for
 *     SRFax, and indistinguishable on screen;
 *   - "no outbound mail account" read as "stop looking". It does NOT mean that. An existing
 *     eFax SMTP job collects its send notifications from the INBOUND mailbox and must keep
 *     doing so, so an unset outbound mail account is phrased as *where the confirmation is
 *     looked for instead*, never as an absence of collection.
 *
 * Every function here is pure and takes plain data, so `tests/test_leg_mailbox_display.py`
 * EXECUTES it under node over all four permutations rather than grepping the JSX that renders
 * it.
 */

/**
 * The `provider_account.account_kind` that IS a mailbox — `backend/providers/schema.py`'s
 * `ACCOUNT_KIND_MAIL`. One kind across every brand, because the mailbox is ours: filtering by
 * provider would hide the one record several providers' legs are supposed to share.
 */
export const MAIL_ACCOUNT_KIND = 'mail'

/** The fields of a provider account this module reads. A subset of `ProviderAccountSummary`. */
export interface MailboxAccount {
  id: string
  account_kind: string
  display_name: string
}

/** The account list has not been read — LOADING or FAILED. Never rendered as "no mailbox". */
export const MAILBOX_NOT_LOADED = 'not_loaded'
/** This leg names a `mail` account and the loaded list contains it. */
export const MAILBOX_NAMED = 'named'
/** This leg names an account the LOADED list does not contain — deleted, or another kind. */
export const MAILBOX_DANGLING = 'dangling'
/** This leg names an account that exists and is NOT a mailbox: it reads no mailbox at all. */
export const MAILBOX_NOT_A_MAILBOX = 'not_a_mailbox'
/** This leg names no account. For the outbound leg this is a first-class, valid choice. */
export const MAILBOX_NONE = 'none'

/** One leg's mailbox, resolved against the account list. */
export interface LegMailbox {
  /** One of the `MAILBOX_*` values above. */
  status: string
  /** The id the leg names, or null. Present even when it resolves to nothing. */
  accountId: string | null
  /** The mailbox's display name. Null on every status except `named`. */
  displayName: string | null
  /** The named account's kind when it is NOT a mailbox — so the row can say what it is. */
  otherKind: string | null
}

/**
 * Resolve one leg's mailbox from the id it names and the account list.
 *
 * `accounts === null` means the list HAS NOT BEEN READ, and that is deliberately a distinct
 * input from `[]`: conflating the two is the defect `accountSelectorState.ts` was written to
 * remove — a list still in flight rendered identically to a configuration that is genuinely
 * empty, so a transient state and one needing repair looked the same.
 */
export function resolveLegMailbox(
  accountId: string | null | undefined,
  accounts: MailboxAccount[] | null,
): LegMailbox {
  const id = (accountId || '').trim()
  if (!id) {
    return { status: MAILBOX_NONE, accountId: null, displayName: null, otherKind: null }
  }
  if (accounts === null) {
    return { status: MAILBOX_NOT_LOADED, accountId: id, displayName: null, otherKind: null }
  }
  const row = accounts.find(a => a.id === id)
  if (!row) {
    return { status: MAILBOX_DANGLING, accountId: id, displayName: null, otherKind: null }
  }
  if (row.account_kind !== MAIL_ACCOUNT_KIND) {
    // NOT a gap and NOT a fault: an eFax API inbound leg legitimately names an `efax_api`
    // account and downloads its faxes over HTTP. Saying so beats a blank cell, which cannot
    // distinguish "reads no mailbox" from "reads one we failed to name".
    return {
      status: MAILBOX_NOT_A_MAILBOX,
      accountId: id,
      displayName: null,
      otherKind: row.account_kind,
    }
  }
  return {
    status: MAILBOX_NAMED,
    accountId: id,
    displayName: row.display_name,
    otherKind: null,
  }
}

/** Whether this leg resolved to an actual mailbox. */
export function hasMailbox(mailbox: LegMailbox): boolean {
  return mailbox.status === MAILBOX_NAMED
}

/**
 * THE SAME MAILBOX ON BOTH LEGS — a VALID configuration, not a warning state.
 *
 * eFax works this way today and SFax turned out to as well. It is reported so the row can SAY
 * one box carries both, which is a fact an operator diagnosing a missing confirmation needs;
 * it is never a validation and nothing in this module discourages it.
 */
export function sharesMailbox(outbound: LegMailbox, inbound: LegMailbox): boolean {
  return (
    hasMailbox(outbound) &&
    hasMailbox(inbound) &&
    outbound.accountId === inbound.accountId
  )
}

/**
 * The INBOUND leg's mailbox row value.
 *
 * A leg that reads no mailbox says which record it reads instead, and a leg whose account
 * cannot be resolved says that rather than rendering empty.
 */
export function inboundMailboxText(inbound: LegMailbox): string {
  switch (inbound.status) {
    case MAILBOX_NAMED:
      return `${inbound.displayName} — received faxes arrive here`
    case MAILBOX_NOT_A_MAILBOX:
      return `no mailbox — this leg reads a ${inbound.otherKind} account, not a mailbox`
    case MAILBOX_DANGLING:
      return `⚠️ names an account that is not in the loaded list (${inbound.accountId})`
    case MAILBOX_NOT_LOADED:
      return '⚠️ not established — the account list has not been read'
    default:
      return 'none named for this leg'
  }
}

/**
 * The OUTBOUND leg's mailbox row value — and the one that must not render blank.
 *
 * An unset outbound mail account is phrased by what happens INSTEAD, which depends on the
 * inbound leg. That dependency is the whole point: the identical stored state (NULL) means
 * "the confirmation is looked for in the inbound box" on an eFax job and "nothing collects the
 * confirmation" on an API-plus-webhook job with no mailbox anywhere, and only the second is a
 * problem to raise.
 */
export function outboundMailboxText(outbound: LegMailbox, inbound: LegMailbox): string {
  switch (outbound.status) {
    case MAILBOX_NAMED:
      return sharesMailbox(outbound, inbound)
        ? `${outbound.displayName} — the same mailbox as the inbound leg, which is valid`
        : `${outbound.displayName} — send confirmations arrive here`
    case MAILBOX_NOT_A_MAILBOX:
      return `⚠️ names a ${outbound.otherKind} account, which is not a mailbox`
    case MAILBOX_DANGLING:
      return `⚠️ names an account that is not in the loaded list (${outbound.accountId})`
    case MAILBOX_NOT_LOADED:
      return '⚠️ not established — the account list has not been read'
    default:
      break
  }
  // NONE — three different situations, and the difference is which mailbox, if any, is left.
  if (hasMailbox(inbound)) {
    return `none named — send confirmations are looked for in the inbound mailbox (${inbound.displayName})`
  }
  if (inbound.status === MAILBOX_NOT_LOADED) {
    return 'none named — send confirmations are looked for in the inbound mailbox, which could not be read'
  }
  return '⚠️ none named, and this job has no inbound mailbox either — no mailbox is watched for a send confirmation'
}

/**
 * The sentence shown UNDER the outbound row, or null when none is needed.
 *
 * Rendered as text and not as a tooltip, for `apiEndpoint.ts`'s reason: the state this row
 * exists to expose is one an operator has to act on, and a hover is not a surface anybody
 * finds. `null` for a configuration that needs no commentary — including the shared-mailbox
 * case, which is stated in the value and needs no caveat.
 */
export function outboundMailboxDetail(outbound: LegMailbox, inbound: LegMailbox): string | null {
  if (outbound.status === MAILBOX_NONE && !hasMailbox(inbound)) {
    if (inbound.status === MAILBOX_NOT_LOADED) return null
    return (
      'A provider that confirms a send by email has nowhere to deliver it on this job. That is ' +
      'harmless if this leg is closed by a webhook or a status poll, and invisible if it is not — ' +
      'the job still sends, and the confirmation is simply never collected. Name an outbound ' +
      'notification mailbox in the job editor if this provider mails its confirmations.'
    )
  }
  if (outbound.status === MAILBOX_DANGLING) {
    return (
      'The account list HAS loaded, so this will not resolve on its own: repoint the leg in the ' +
      'job editor, or recreate the record under Settings → Provider Accounts.'
    )
  }
  if (outbound.status === MAILBOX_NOT_A_MAILBOX) {
    return (
      'A send-notification mailbox is a mail-kind account. This reference cannot be read as a ' +
      'mailbox, so no confirmation is collected from it.'
    )
  }
  return null
}
